import {cellElementToRequirement, type FillableCellElement} from '../lib/cell-fill';
import {
  TextRenderer,
  PhotoRenderer,
  CheckboxRenderer,
  DropdownRenderer,
  SignatureRenderer,
} from './renderers';
import {cellMarkRoom} from './SlotMarks';
import {type ElemProps} from './renderers/element-props';
import {buildCellStyle} from '../lib/table-ops';
import type {TableCellStyle} from '../lib/table-types';

/**
 * A fillable table cell element, rendered by the SAME renderers that serve
 * elements outside the table.
 *
 * This deliberately imports the individual renderers rather than `PreviewElement`:
 * `PreviewElement` imports `TableElement`, so going through it would close an
 * import cycle. `PreviewRenderers` has no table dependency, so the seam is here.
 *
 * Only the fillable types reach this component — `heading` and `logo` are
 * decorative and stay on `CellElementView` (see `isFillableCellElement`).
 */
export function CellElementFill({
  element,
  cellKey,
  cellStyle,
  ...fill
}: Omit<ElemProps, 'label' | 'fillMode' | 'inlineEdit' | 'onChangeElement' | 'textStyle'> & {
  element: FillableCellElement;
  /** The element's address in the flat fill map — also the synthetic id. */
  cellKey: string;
  /** The cell's own style, for the typography its control renders text in —
   *  see `ElemProps.textStyle`. Raw rather than built, so `buildCellStyle`
   *  remains the single reader of it. */
  cellStyle?: TableCellStyle;
}) {
  // A cell element renders its CONTROL ONLY — no label. Inside a table the
  // labelling belongs to the table: the header row names the column, and an
  // author who wants text beside a control puts a `heading` cell element (whose
  // whole purpose is a label) in the neighbouring cell. Repeating the element's
  // own label inside the cell just duplicated that and ate the column's width.
  //
  // A cell element the author never sized is sized by its COLUMN -- that is what
  // `CellElementView` draws while they design it, and the column edge is the
  // knob they actually reach for. Without this the fill canvas substituted a
  // flat 110px, so a photo stretched across a wide column came out narrow on the
  // filled report and in its PDF, with nothing on either screen explaining the
  // difference.
  //
  // Read once rather than per arm: it is one rule, and the two renderers that
  // honour it were asking the same question separately. Narrowed to the two
  // types that HAVE a width rather than reaching for one on the union -- a text
  // input or a checkbox has no box to stretch, so there is nothing to ask.
  const spanWidth =
    (element.type === 'photo' || element.type === 'signature') &&
    element.config.width === undefined;
  const props: ElemProps = {
    ...fill,
    label: '',
    fillMode: true,
    spanWidth,
    textStyle: buildCellStyle(cellStyle).text,
  };

  // Switching on the CELL element (not the synthetic requirement) is what keeps
  // this cast-free: `cellElementToRequirement` is declared to return
  // `RequirementOf<T['type']>`, so each arm hands its renderer the one variant
  // it accepts.
  const body = (() => {
    switch (element.type) {
      case 'text':
        return <TextRenderer {...props} item={cellElementToRequirement(element, cellKey)} />;
      case 'photo':
        return <PhotoRenderer {...props} item={cellElementToRequirement(element, cellKey)} />;
      case 'checkbox':
        return <CheckboxRenderer {...props} item={cellElementToRequirement(element, cellKey)} />;
      case 'dropdown':
        return <DropdownRenderer {...props} item={cellElementToRequirement(element, cellKey)} />;
      case 'signature':
        return <SignatureRenderer {...props} item={cellElementToRequirement(element, cellKey)} />;
    }
  })();

  // `max-w-full` + `overflow-hidden`: a column can be narrower than a photo or
  // signature box, and the page must never scroll sideways because of a cell.
  // That clip is also what ate the out-of-box markers, hence the room reserved
  // for them — `markRoom` owns those offsets, because this is the second
  // clipping ancestor and the two must not drift (see `cellMarkRoom`). Each
  // renderer above draws its own marks, so only the room is needed here.
  const room = cellMarkRoom(element);
  return (
    <div className={`max-w-full space-y-0.5 overflow-hidden${room ? ` ${room}` : ''}`}>{body}</div>
  );
}
